import React, { useEffect, useState } from 'react';
import { NFC_UI_VISIBLE } from '@/lib/nfcUiVisibility';
import { canConfigureHaccp } from '@/features/haccp/haccpAccess';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Archive,
  BarChart3,
  Bell,
  Boxes,
  Building2,
  CalendarDays,
  ClipboardCheck,
  ChevronDown,
  ChevronRight,
  CircleCheck,
  Clock3,
  FileText,
  Files,
  LayoutDashboard,
  Loader2,
  MapPin,
  Menu as MenuIcon,
  PackageCheck,
  ShieldAlert,
  SlidersHorizontal,
  Settings2,
  Store,
  Truck,
  UserRound,
  Users,
  X,
} from 'lucide-react';
import NotificationsBell from '../components/NotificationsBell';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { hasAnyCapUnder, hasCap, isGlobalCeo, isPureEmployee } from '../lib/capabilities';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { useEffectiveMe } from '../lib/effectiveMe';
import { apiFetch } from '../lib/supabaseClient';
import EmployeeDashboard from './EmployeeDashboard';
import { roleDisplayLabel } from '../lib/roleDisplayLabel';
import '../styles/foundation-dashboard.css';

const UNAVAILABLE = 'Non disponibile';
const KPI_UNAVAILABLE = '—';

const roleLabels = {
  CEO: 'CEO',
  TITOLARE: 'Titolare',
  RESP_GEN: 'Responsabile generale',
  RESP_LOCALE: 'Responsabile locale',
  SEGRETERIA: 'Segreteria',
  DIPENDENTE: 'Dipendente',
};

const formatInteger = (value) => (
  typeof value === 'number' && Number.isFinite(value)
    ? new Intl.NumberFormat('it-IT').format(value)
    : UNAVAILABLE
);

const formatKpiValue = (value) => (
  typeof value === 'number' && Number.isFinite(value) ? formatInteger(value) : KPI_UNAVAILABLE
);

const formatCountLabel = (value, singular, plural) => (
  `${formatInteger(value)} ${value === 1 ? singular : plural}`
);

const formatDate = (value) => {
  if (!value) return UNAVAILABLE;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return UNAVAILABLE;
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null);

const getRoleLabel = (me) => {
  const role = firstDefined(
    me?.role,
    me?.role_code,
    Array.isArray(me?.role_codes) ? me.role_codes[0] : null,
  );
  return roleLabels[role] || role || 'Titolare';
};


const getInitials = (me) => {
  const name = firstDefined(me?.full_name, me?.name, me?.email, 'T');
  const words = String(name).trim().split(/\s+/).filter(Boolean);
  if (words.length > 1) return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
  return String(words[0] || 'T').slice(0, 2).toUpperCase();
};

const getCollection = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.requests)) return value.requests;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const readApiPayload = async (result) => (
  result && typeof result.json === 'function' ? result.json() : result
);

const getItemCount = (value) => {
  if (typeof value === 'number') return value;
  if (Array.isArray(value)) return value.length;
  if (value && typeof value.count === 'number') return value.count;
  return null;
};

const isPending = (item) => {
  const status = String(firstDefined(item?.status, item?.state, item?.approval_status, '')).toLowerCase();
  return ['pending', 'in_attesa', 'da_approvare', 'requested', 'open'].includes(status);
};

const getCompanyId = (company) => firstDefined(company?.id, company?.company_id, company?.organization_id);
const getLocationId = (location) => firstDefined(location?.id, location?.location_id);
const getDepartmentId = (department) => firstDefined(department?.id, department?.department_id);
const getName = (item, fallback = UNAVAILABLE) => firstDefined(
  item?.name,
  item?.nome,
  item?.label,
  item?.display_name,
  item?.company_name,
  item?.location_name,
  item?.department_name,
  fallback,
);

const sameValue = (left, right) => (
  left !== undefined
  && left !== null
  && right !== undefined
  && right !== null
  && String(left).toLowerCase() === String(right).toLowerCase()
);

const matchesScopedItem = (item, getId, scopeId, scopeItem) => {
  if (!scopeId) return true;
  const itemIds = [getId(item), item?.company_id, item?.organization_id, item?.location_id, item?.slug];
  const scopeIds = [scopeId, scopeItem?.id, scopeItem?.slug];
  if (itemIds.some((itemId) => scopeIds.some((expectedId) => sameValue(itemId, expectedId)))) return true;
  const itemNames = [getName(item), item?.slug];
  const scopeNames = [getName(scopeItem), scopeItem?.nome, scopeItem?.name, scopeItem?.slug];
  return itemNames.some((itemName) => scopeNames.some((scopeName) => sameValue(itemName, scopeName)));
};

const getPersonName = (person) => firstDefined(
  person?.display_name,
  `${person?.first_name || ''} ${person?.last_name || ''}`.trim(),
  person?.legacy_id,
  UNAVAILABLE,
);

const getPersonId = (person) => firstDefined(person?.legacy_id, person?.id);

const countActivePeople = (rows) => (
  Array.isArray(rows) ? rows.reduce((total, row) => total + (row?.active === false ? 0 : 1), 0) : null
);

function ScopePath({ selectedCompany, selectedLocation, selectedDepartment, onReset, onClearLocation, onClearDepartment }) {
  return (
    <div className="cc-path" aria-label="Percorso del perimetro" data-testid="dashboard-scope-path">
      <button
        type="button"
        className="cc-path-button"
        onClick={onReset}
        disabled={!selectedCompany}
        data-testid="dashboard-path-company"
      >
        <Building2 size={13} aria-hidden="true" />
        <span>{selectedCompany ? getName(selectedCompany) : 'Tutte le aziende'}</span>
      </button>
      <ChevronRight size={13} aria-hidden="true" />
      <button
        type="button"
        className="cc-path-button"
        onClick={onClearLocation}
        disabled={!selectedLocation}
        data-testid="dashboard-path-location"
      >
        <MapPin size={13} aria-hidden="true" />
        <span>{selectedLocation ? getName(selectedLocation) : 'Tutti i locali'}</span>
      </button>
      <ChevronRight size={13} aria-hidden="true" />
      <button
        type="button"
        className="cc-path-button"
        onClick={onClearDepartment}
        disabled={!selectedDepartment}
        data-testid="dashboard-path-department"
      >
        <Users size={13} aria-hidden="true" />
        <span>{selectedDepartment ? getName(selectedDepartment) : 'Tutti i reparti'}</span>
      </button>
    </div>
  );
}

function ExplorerRow({ icon: Icon, label, detail, onClick, testId, linkTo }) {
  const content = (
    <>
      <span className="cc-company-logo"><Icon size={16} aria-hidden="true" /></span>
      <span>
        <b>{label}</b>
        <small>{detail}</small>
      </span>
      <ChevronRight size={17} aria-hidden="true" />
    </>
  );

  if (linkTo) {
    return <Link className="cc-company cc-explorer-row" to={linkTo} data-testid={testId}>{content}</Link>;
  }

  return (
    <button className="cc-company cc-explorer-row" type="button" onClick={onClick} data-testid={testId}>
      {content}
    </button>
  );
}

function PerimeterExplorer({
  visibleCompanies,
  visibleLocations,
  visibleDepartments,
  selectedCompanyId,
  selectedLocationId,
  selectedDepartmentId,
  selectedCompany,
  selectedLocation,
  selectedDepartment,
  locationsAvailable,
  people,
  activePeople,
  personnelRows,
  personnelLoading,
  personnelError,
  canSeePersonale,
  onSelectCompany,
  onSelectLocation,
  onSelectDepartment,
  onBack,
  onReset,
  onClearLocation,
  onClearDepartment,
}) {
  const selectedCompanyName = selectedCompany && getName(selectedCompany);
  const selectedLocationName = selectedLocation && getName(selectedLocation);
  const selectedDepartmentName = selectedDepartment && getName(selectedDepartment);
  const companyPersonnel = Array.isArray(personnelRows) && selectedCompanyName
    ? personnelRows.filter((person) => person.company_name === selectedCompanyName)
    : null;
  const locationPersonnel = Array.isArray(companyPersonnel) && selectedLocationName
    ? companyPersonnel.filter((person) => person.primary_location === selectedLocationName)
    : null;
  const scopedPersonnel = Array.isArray(locationPersonnel) && selectedDepartmentName
    ? locationPersonnel.filter((person) => person.department === selectedDepartmentName)
    : locationPersonnel;
  const selectedCompanyPeople = selectedCompany
    ? getItemCount(people?.by_company?.[getCompanyId(selectedCompany)]?.active
      ?? people?.by_company?.[String(getCompanyId(selectedCompany))]?.active)
    : null;
  const countDetail = (count, loading = false) => (
    loading ? 'Caricamento persone…' : count === null ? UNAVAILABLE : formatCountLabel(count, 'persona', 'persone')
  );
  const noChildState = (label) => (
    <EmptyState>
      <Users size={18} />
      <span><strong>{label}</strong><small>Nessun dato disponibile nel perimetro autorizzato.</small></span>
    </EmptyState>
  );
  const companyPeopleCount = companyPersonnel === null
    ? selectedCompanyPeople
    : countActivePeople(companyPersonnel);
  const locationPeopleCount = locationPersonnel === null ? null : countActivePeople(locationPersonnel);
  const scopedPeopleCount = scopedPersonnel === null ? null : countActivePeople(scopedPersonnel);
  const scopeSummary = selectedDepartment
    ? `${selectedDepartmentName || UNAVAILABLE} · ${countDetail(scopedPeopleCount, personnelLoading)}`
    : selectedLocation
      ? `${selectedLocationName || UNAVAILABLE} · ${formatCountLabel(visibleDepartments.length, 'reparto', 'reparti')} · ${countDetail(locationPeopleCount, personnelLoading)}`
      : selectedCompany
        ? `${selectedCompanyName || UNAVAILABLE} · ${locationsAvailable ? `${visibleLocations.length} ${visibleLocations.length === 1 ? 'locale' : 'locali'}` : UNAVAILABLE} · ${countDetail(companyPeopleCount, personnelLoading)}`
        : [
          formatCountLabel(visibleCompanies.length, 'azienda', 'aziende'),
          locationsAvailable ? formatCountLabel(visibleLocations.length, 'locale', 'locali') : UNAVAILABLE,
          formatCountLabel(getItemCount(activePeople), 'persona', 'persone'),
        ].join(' · ');

  const renderPeople = () => {
    if (!canSeePersonale) {
      return (
        <EmptyState>
          <ShieldAlert size={18} />
          <span><strong>Persone non disponibili</strong><small>La capability Personale non è disponibile per questo account.</small></span>
        </EmptyState>
      );
    }
    if (personnelLoading) {
      return <div className="cc-explorer-status"><Loader2 size={17} className="cc-spin" /> Caricamento persone…</div>;
    }
    if (personnelError) {
      return (
        <EmptyState>
          <ShieldAlert size={18} />
          <span><strong>Elenco persone non disponibile</strong><small>{personnelError}</small></span>
        </EmptyState>
      );
    }
    if (!scopedPersonnel?.length) return noChildState('Nessuna persona in questo reparto');

    return (
      <>
        <div className="cc-explorer-detail-heading">
          <strong>{selectedDepartment ? getName(selectedDepartment) : UNAVAILABLE}</strong>
          <small>{countDetail(scopedPeopleCount)}</small>
        </div>
        <div className="cc-company-list" aria-label="Persone nel reparto" data-testid="dashboard-people-list">
          {scopedPersonnel.map((person) => {
            const personId = getPersonId(person);
            const detail = [person.job_title, person.lifecycle_status].filter(Boolean).join(' · ') || 'Dettagli non disponibili';
            return (
              <ExplorerRow
                key={String(personId)}
                icon={UserRound}
                label={getPersonName(person)}
                detail={detail}
                linkTo={personId ? `/personale/${personId}` : undefined}
                testId={`dashboard-person-${personId}`}
              />
            );
          })}
        </div>
      </>
    );
  };

  const renderCompanies = () => (
    visibleCompanies.length > 0 ? (
      <div className="cc-company-list" aria-label="Aziende autorizzate">
        {visibleCompanies.map((company) => {
          const companyId = getCompanyId(company);
          const companyLocations = visibleLocations.filter((location) => (
            String(firstDefined(location.company_id, location.organization_id)) === String(companyId)
          ));
          const companyPeople = people?.by_company?.[companyId] || people?.by_company?.[String(companyId)];
          const peopleCount = getItemCount(companyPeople?.active ?? companyPeople);
          return (
            <ExplorerRow
              key={String(companyId)}
              icon={Building2}
              label={getName(company)}
              detail={`${locationsAvailable ? `${companyLocations.length} ${companyLocations.length === 1 ? 'locale' : 'locali'}` : UNAVAILABLE} · ${peopleCount === null ? UNAVAILABLE : `${peopleCount} ${peopleCount === 1 ? 'persona' : 'persone'}`}`}
              onClick={() => onSelectCompany(String(companyId))}
              testId={`dashboard-company-${companyId}`}
            />
          );
        })}
      </div>
    ) : noChildState('Nessuna azienda disponibile')
  );

  const renderCompany = () => (
    <>
      <div className="cc-explorer-detail-heading">
        <strong>{selectedCompany ? getName(selectedCompany) : UNAVAILABLE}</strong>
        <small>{locationsAvailable ? `${visibleLocations.length} ${visibleLocations.length === 1 ? 'locale' : 'locali'}` : UNAVAILABLE} · {countDetail(selectedCompanyPeople)}</small>
      </div>
      {visibleLocations.length > 0 ? (
        <div className="cc-company-list" aria-label="Locali dell'azienda" data-testid="dashboard-location-list">
          {visibleLocations.map((location) => {
            const locationName = getName(location);
            const locationPeople = Array.isArray(personnelRows)
              ? countActivePeople(personnelRows.filter((person) => (
                person.company_name === selectedCompanyName && person.primary_location === locationName
              )))
              : null;
            return (
              <ExplorerRow
                key={String(getLocationId(location))}
                icon={MapPin}
                label={locationName}
                detail={countDetail(locationPeople, personnelLoading)}
                onClick={() => onSelectLocation(String(getLocationId(location)))}
                testId={`dashboard-location-${getLocationId(location)}`}
              />
            );
          })}
        </div>
      ) : noChildState('Nessun locale disponibile')}
    </>
  );

  const renderLocation = () => (
    <>
      <div className="cc-explorer-detail-heading">
        <strong>{selectedLocation ? getName(selectedLocation) : UNAVAILABLE}</strong>
        <small>{countDetail(getItemCount(activePeople), personnelLoading && activePeople === null)}</small>
      </div>
      {visibleDepartments.length > 0 ? (
        <div className="cc-company-list" aria-label="Reparti del locale" data-testid="dashboard-department-list">
          {visibleDepartments.map((department) => {
            const departmentName = getName(department);
            const departmentPeople = Array.isArray(personnelRows)
              ? countActivePeople(personnelRows.filter((person) => (
                person.company_name === selectedCompanyName
                && person.primary_location === selectedLocationName
                && person.department === departmentName
              )))
              : null;
            return (
              <ExplorerRow
                key={String(getDepartmentId(department))}
                icon={Users}
                label={departmentName}
                detail={countDetail(departmentPeople, personnelLoading)}
                onClick={() => onSelectDepartment(String(getDepartmentId(department)))}
                testId={`dashboard-department-${getDepartmentId(department)}`}
              />
            );
          })}
        </div>
      ) : noChildState('Nessun reparto disponibile')}
    </>
  );

  return (
    <section className="cc-card cc-explorer">
      <div className="cc-panel-heading">
        <div>
          <span className="cc-panel-label">Perimetro aziendale</span>
           <h2>{scopeSummary}</h2>
        </div>
        {(selectedCompany || selectedLocation || selectedDepartment) && (
          <span className="cc-explorer-level">
            {selectedDepartment ? 'Persone' : selectedLocation ? 'Reparti' : 'Locali'}
          </span>
        )}
      </div>
      {(selectedCompany || selectedLocation || selectedDepartment) && (
        <button className="cc-explorer-back" type="button" onClick={onBack} data-testid="dashboard-explorer-back">
          <ArrowLeft size={14} aria-hidden="true" />
          Indietro
        </button>
      )}
      <div className="cc-selects">
        <ScopeFilter
          label="Azienda"
          value={selectedCompanyId}
          onChange={onSelectCompany}
          testId="dashboard-company-filter"
          options={[
            { value: 'all', label: visibleCompanies.length ? 'Tutte le aziende' : UNAVAILABLE },
            ...visibleCompanies.map((company) => ({ value: String(getCompanyId(company)), label: getName(company) })),
          ]}
          disabled={!visibleCompanies.length}
        />
        <ScopeFilter
          label="Locale"
          value={selectedLocationId}
          onChange={onSelectLocation}
          testId="dashboard-location-filter"
          options={[
            { value: 'all', label: visibleLocations.length ? 'Tutti i locali' : UNAVAILABLE },
            ...visibleLocations.map((location) => ({ value: String(getLocationId(location)), label: getName(location) })),
          ]}
          disabled={selectedCompanyId === 'all' || !visibleLocations.length}
        />
        <ScopeFilter
          label="Reparto"
          value={selectedDepartmentId}
          onChange={onSelectDepartment}
          testId="dashboard-department-filter"
          options={[
            { value: 'all', label: visibleDepartments.length ? 'Tutti i reparti' : UNAVAILABLE },
            ...visibleDepartments.map((department) => ({ value: String(getDepartmentId(department)), label: getName(department) })),
          ]}
          disabled={selectedCompanyId === 'all' || selectedLocationId === 'all' || !visibleDepartments.length}
        />
      </div>
      <ScopePath
        selectedCompany={selectedCompany}
        selectedLocation={selectedLocation}
        selectedDepartment={selectedDepartment}
        onReset={onReset}
        onClearLocation={onClearLocation}
        onClearDepartment={onClearDepartment}
      />
      <div className="cc-explorer-content">
        {selectedDepartmentId !== 'all'
          ? renderPeople()
          : selectedLocationId !== 'all'
            ? renderLocation()
            : selectedCompanyId !== 'all'
              ? renderCompany()
              : renderCompanies()}
      </div>
    </section>
  );
}

function MetricCard({ icon: Icon, label, value, detail, tone = 'default', testId }) {
  return (
    <article className={`cc-metric-card cc-metric-${tone}`} data-testid={testId}>
      <div className="cc-metric-icon" aria-hidden="true">
        <Icon size={19} strokeWidth={1.8} />
      </div>
      <div className="cc-metric-content">
        <span className="cc-metric-label">{label}</span>
        <strong className={value === KPI_UNAVAILABLE ? 'cc-value-unavailable' : ''}>{value}</strong>
        {detail && <span className="cc-metric-detail">{detail}</span>}
      </div>
    </article>
  );
}

function ScopeFilter({ label, value, options, disabled, onChange, testId }) {
  return (
    <label className={`cc-scope-filter ${disabled ? 'is-disabled' : ''}`}>
      <span>{label}</span>
      <span className="cc-select-wrap">
        <select
          aria-label={label}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          data-testid={testId}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <ChevronDown size={15} aria-hidden="true" />
      </span>
    </label>
  );
}

function EmptyState({ children }) {
  return <div className="cc-empty-state">{children}</div>;
}

function ActionRow({ icon: Icon, label, detail, to, disabled, testId }) {
  const content = (
    <>
      <span className="cc-action-icon" aria-hidden="true"><Icon size={17} /></span>
      <span className="cc-action-copy">
        <strong>{label}</strong>
        {detail && <small>{detail}</small>}
      </span>
      {disabled ? <span className="cc-badge">In arrivo</span> : <ArrowRight size={16} aria-hidden="true" />}
    </>
  );

  if (disabled || !to) {
    return <div className="cc-action-row is-disabled" aria-disabled="true" data-testid={testId}>{content}</div>;
  }

  return <Link className="cc-action-row" to={to} data-testid={testId}>{content}</Link>;
}

function SectionList({ title, items, className = '', testId }) {
  return (
    <section className={`cc-section ${className}`.trim()} data-testid={testId}>
      <div className="cc-section-title">{title}</div>
      <div className="cc-list">
        {items.map((item) => (
          <ActionRow
            key={item.label}
            icon={item.icon}
            label={item.label}
            detail={item.detail}
            to={item.to}
            disabled={item.future}
            testId={item.testId}
          />
        ))}
      </div>
    </section>
  );
}

function SectionHeader({ eyebrow, title, to, actionLabel = 'Vedi tutto' }) {
  return (
    <div className="cc-section-heading">
      <div>
        {eyebrow && <span className="cc-eyebrow">{eyebrow}</span>}
        <h2>{title}</h2>
      </div>
      {to && <Link className="cc-section-link" to={to}>{actionLabel}<ArrowRight size={15} /></Link>}
    </div>
  );
}

function DashboardSidebar({ sections, onClose }) {
  return (
    <>
      <button
        className="cc-sidebar-backdrop"
        type="button"
        aria-label="Chiudi navigazione"
        onClick={onClose}
        data-testid="dashboard-sidebar-backdrop"
      />
      <aside className="cc-desktop-sidebar" aria-label="Navigazione Dashboard" data-testid="dashboard-sidebar">
        <div className="cc-sidebar-brand">
          <img src="/brand/restaurantos-logo-horizontal.svg" alt="RestaurantOS" />
          <button type="button" className="cc-icon-button" onClick={onClose} aria-label="Chiudi navigazione">
            <X size={18} />
          </button>
        </div>
        <nav className="cc-sidebar-nav">
          <Link className="cc-sidebar-home" to="/foundation" onClick={onClose} data-testid="dashboard-sidebar-home">
            <LayoutDashboard size={17} aria-hidden="true" />
            <span>Dashboard</span>
          </Link>
          {sections.map((section) => (
            <section className="cc-sidebar-section" key={section.title}>
              <span className="cc-sidebar-section-title">{section.title}</span>
              {section.items.map((item) => {
                const Icon = item.icon;
                if (item.action) {
                  return (
                    <button className="cc-sidebar-item cc-sidebar-action" type="button" onClick={item.action} key={item.label}>
                      <Icon size={16} aria-hidden="true" />
                      <span>{item.label}</span>
                      <ArrowRight size={14} aria-hidden="true" />
                    </button>
                  );
                }
                if (item.disabled || !item.to) {
                  return (
                    <span className="cc-sidebar-item is-disabled" aria-disabled="true" key={item.label}>
                      <Icon size={16} aria-hidden="true" />
                      <span>{item.label}</span>
                      {item.detail && <small>{item.detail}</small>}
                    </span>
                  );
                }
                return (
                  <Link className="cc-sidebar-item" to={item.to} onClick={onClose} key={item.label} data-testid={item.testId}>
                    <Icon size={16} aria-hidden="true" />
                    <span>{item.label}</span>
                    <ArrowRight size={14} aria-hidden="true" />
                  </Link>
                );
              })}
            </section>
          ))}
        </nav>
        <div className="cc-sidebar-footer">
          <ShieldAlert size={14} aria-hidden="true" />
          <span>Accessi filtrati dal tuo perimetro autorizzato.</span>
        </div>
      </aside>
    </>
  );
}

function StructureSummary({ companies, locations, departments, people, canOpen }) {
  const items = [
    { label: 'Aziende', value: companies, icon: Building2 },
    { label: 'Locali', value: locations, icon: MapPin },
    { label: 'Reparti', value: departments, icon: Users },
    { label: 'Persone', value: people, icon: UserRound },
  ];
  return (
    <section className="cc-card cc-structure-summary" data-testid="dashboard-structure-summary">
      <SectionHeader eyebrow="PERIMETRO" title="Struttura aziendale" />
      <div className="cc-structure-grid">
        {items.map(({ label, value, icon: Icon }) => (
          <div className="cc-structure-item" key={label}>
            <span className="cc-structure-icon" aria-hidden="true"><Icon size={16} /></span>
            <strong>{formatKpiValue(value)}</strong>
            <span>{label}</span>
          </div>
        ))}
      </div>
      <div className="cc-structure-footer">
        <p className="cc-structure-note">Numeri del perimetro autorizzato, aggiornati dal server.</p>
        {canOpen && (
          <Link className="cc-section-link" to="/impostazioni/struttura-aziendale">
            Apri struttura <ArrowRight size={14} aria-hidden="true" />
          </Link>
        )}
      </div>
    </section>
  );
}

function OperationalOverview({ items }) {
  return (
    <section className="cc-card cc-operational-overview" data-testid="dashboard-operational-overview">
      <SectionHeader eyebrow="OPERATIVITÀ" title="Attività del tenant" />
      {items.length > 0 ? (
        <div className="cc-operational-grid">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <Link className="cc-operational-item" to={item.to} key={item.label}>
                <span className="cc-operational-icon"><Icon size={18} aria-hidden="true" /></span>
                <span className="cc-operational-copy">
                  <strong>{item.label}</strong>
                  <small>{item.detail}</small>
                </span>
                <ArrowRight size={15} aria-hidden="true" />
              </Link>
            );
          })}
        </div>
      ) : (
        <EmptyState>
          <ShieldAlert size={18} />
          <span><strong>Nessuna attività disponibile</strong><small>Le funzioni operative dipendono dalle capability effettive.</small></span>
        </EmptyState>
      )}
    </section>
  );
}

function CompanyArea({ me, onlyHaccp = false }) {
  const groups = [
    {
      title: 'Operatività',
      items: [
        { label: 'Costi', icon: BarChart3 },
        { label: 'Magazzino', icon: Boxes },
        { label: 'Ordini', icon: PackageCheck },
        { label: 'Fornitori', icon: Store },
      ],
    },
    {
      title: 'Controllo',
      items: [
        {
          label: 'HACCP',
          icon: ClipboardCheck,
          detail: 'Configurazione e controlli HACCP',
          to: '/haccp',
          canView: canConfigureHaccp,
          testId: 'dashboard-company-area-haccp',
        },
        { label: 'Manutenzioni', icon: Settings2 },
        { label: 'Mezzi', icon: Truck },
        { label: 'Documenti', icon: FileText },
      ],
    },
  ];

  return (
    <section className="cc-card cc-company-area" data-testid="dashboard-company-area">
      <SectionHeader eyebrow="AREA AZIENDA" title="AREA AZIENDA" />
      <div className="cc-company-area-grid">
        {groups.filter((group) => !onlyHaccp || group.items.some((item) => item.label === 'HACCP')).map((group) => (
          <div className="cc-company-area-group" key={group.title}>
            <span className="cc-company-area-group-title">{group.title}</span>
            <div className="cc-list">
              {group.items
                .filter((item) => (!onlyHaccp || item.label === 'HACCP')
                  && (!item.canView || item.canView(me))
                  && (!item.capability || hasCap(me, item.capability)))
                .map((item) => (
                <ActionRow
                  key={item.label}
                  icon={item.icon}
                  label={item.label}
                  detail={item.detail || 'Modulo in preparazione'}
                  to={item.to}
                  disabled={!item.to}
                  testId={item.testId || `dashboard-company-area-${item.label.toLowerCase()}`}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function TenantClientSwitcher({ tenants, loading, activeTenantId, busy, onEnter, onManageAccess }) {
  return (
    <section id="tenant-clienti" className="cc-card" data-testid="ceo-tenant-switcher">
      <div className="cc-panel-heading">
        <div>
          <span className="cc-panel-label">CEO / FOUNDER</span>
          <h2>Tenant / Clienti</h2>
        </div>
      </div>
      <p className="text-sm text-stone-500 mb-4">
        Gestisci i clienti e accedi ai rispettivi ambienti.
      </p>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-stone-500" data-testid="ceo-tenant-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Caricamento tenant…
        </div>
      ) : (
        <div className="space-y-2">
          {tenants.map((tenant) => (
            <div
              key={tenant.id}
              className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border px-3 py-3 ${
                tenant.id === activeTenantId ? 'border-brand-green bg-emerald-50/60' : 'border-stone-200'
              }`}
              data-testid={`ceo-tenant-${tenant.id}`}
            >
              <div className="min-w-0">
                <strong className="block text-sm text-brand-navy">{tenant.display_name || tenant.name}</strong>
                <span className="text-xs text-stone-500">
                  {tenant.company_count} aziende · {tenant.location_count} locali · {tenant.people_count} persone
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  onClick={() => onEnter(tenant.id)}
                  disabled={busy || tenant.id === activeTenantId}
                  className="bg-brand-green hover:bg-brand-green-hover text-white"
                  data-testid={`ceo-enter-tenant-${tenant.id}`}
                >
                  {tenant.id === activeTenantId ? 'Tenant attivo · CEO' : 'Entra nel tenant'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onManageAccess(tenant.id)}
                  disabled={busy}
                  data-testid={`ceo-manage-access-${tenant.id}`}
                >
                  Gestisci accessi
                </Button>
              </div>
            </div>
          ))}
          {!tenants.length && <div className="text-sm text-stone-500">Nessun tenant disponibile.</div>}
        </div>
      )}
    </section>
  );
}

function AdminFoundationDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('all');
  const [selectedLocationId, setSelectedLocationId] = useState('all');
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('all');
  const [personnelRows, setPersonnelRows] = useState(null);
  const [personnelLoading, setPersonnelLoading] = useState(false);
  const [personnelError, setPersonnelError] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [profileOpen, setProfileOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(false);
  const [ceoTenants, setCeoTenants] = useState([]);
  const [ceoTenantsLoading, setCeoTenantsLoading] = useState(false);
  const [activeTenantId, setActiveTenantId] = useState(null);
  const [tenantContextBusy, setTenantContextBusy] = useState(false);
  const [tenantContextVersion, setTenantContextVersion] = useState(0);
  const appContext = useApp() || {};
  const {
    devImpersonateRoleId,
    devImpersonatePersonaId,
    impersona,
    impersonaPersona,
    ruoli = [],
    dipendenti = [],
    aziende = [],
    locali = [],
    scope = {},
  } = appContext;
  const { me: realMe, refreshMe } = useAuth() || {};
  const effectiveMe = useEffectiveMe();
  const dashboardMe = effectiveMe || me;
  const scopedEmployee = scope?.dipendenteId
    ? dipendenti.find((employee) => sameValue(employee.id, scope.dipendenteId))
    : null;
  const effectiveScopeCompanyId = firstDefined(scope?.aziendaId, scope?.companyId, scopedEmployee?.aziendaId);
  const effectiveScopeLocationId = firstDefined(scope?.localeId, scope?.locationId, scopedEmployee?.localeId);
  const effectiveScopeCompany = aziende.find((company) => sameValue(company.id, effectiveScopeCompanyId));
  const effectiveScopeLocation = locali.find((location) => sameValue(location.id, effectiveScopeLocationId));
  const devSimulationActive = !!devImpersonateRoleId || !!devImpersonatePersonaId;
  const isPlatformScope = realMe?.is_global === true && !activeTenantId && !devSimulationActive;
  // A DEV role simulation must never render the real CEO tenant context as if
  // the simulated user owned the platform console.
  const ceoTenantContextActive = realMe?.is_global === true
    && !!activeTenantId
    && !devSimulationActive;

  useEffect(() => {
    if (!realMe?.is_global) {
      setCeoTenants([]);
      setActiveTenantId(null);
      return undefined;
    }
    let cancelled = false;
    setCeoTenantsLoading(true);
    Promise.all([
      apiFetch('/api/ceo/tenants'),
      apiFetch('/api/ceo/tenant-context'),
    ]).then(([tenants, context]) => {
      if (cancelled) return;
      setCeoTenants(Array.isArray(tenants) ? tenants : []);
      setActiveTenantId(context?.active_tenant_id || null);
    }).catch(() => {
      if (!cancelled) {
        setCeoTenants([]);
        setActiveTenantId(null);
      }
    }).finally(() => {
      if (!cancelled) setCeoTenantsLoading(false);
    });
    return () => { cancelled = true; };
  }, [realMe?.is_global, tenantContextVersion]);

  const enterTenant = async (tenantId) => {
    setTenantContextBusy(true);
    try {
      const context = await apiFetch('/api/ceo/tenant-context', {
        method: 'POST',
        body: JSON.stringify({ tenant_id: tenantId }),
      });
      setActiveTenantId(context.active_tenant_id);
      setSelectedCompanyId('all');
      setSelectedLocationId('all');
      setSelectedDepartmentId('all');
      setTenantContextVersion((version) => version + 1);
      await refreshMe?.();
    } catch (requestError) {
      setError(requestError.message || 'Impossibile entrare nel tenant');
    } finally {
      setTenantContextBusy(false);
    }
  };

  const manageTenantAccess = async (tenantId) => {
    setTenantContextBusy(true);
    try {
      await apiFetch('/api/ceo/tenant-context', {
        method: 'POST',
        body: JSON.stringify({ tenant_id: tenantId }),
      });
      await refreshMe?.();
      navigate('/foundation/users');
    } catch (requestError) {
      setError(requestError.message || 'Impossibile aprire gli accessi del tenant');
    } finally {
      setTenantContextBusy(false);
    }
  };

  const exitTenant = async () => {
    setTenantContextBusy(true);
    try {
      await apiFetch('/api/ceo/tenant-context', { method: 'DELETE' });
      setActiveTenantId(null);
      setSelectedCompanyId('all');
      setSelectedLocationId('all');
      setSelectedDepartmentId('all');
      setTenantContextVersion((version) => version + 1);
      await refreshMe?.();
    } catch (requestError) {
      setError(requestError.message || 'Impossibile tornare alla piattaforma');
    } finally {
      setTenantContextBusy(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    apiFetch('/api/me')
      .then(readApiPayload)
      .then((payload) => {
        if (!cancelled) setMe(payload);
      })
      .catch(() => {
        if (!cancelled) setMe(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (isPlatformScope) {
      setDashboard(null);
      setCompanies([]);
      setLoading(false);
      setRefreshing(false);
      return () => { cancelled = true; };
    }
    const params = new URLSearchParams();
    if (selectedCompanyId !== 'all') params.set('company_id', selectedCompanyId);
    if (selectedLocationId !== 'all') params.set('location_id', selectedLocationId);
    if (selectedDepartmentId !== 'all') params.set('department_id', selectedDepartmentId);
    const query = params.toString();

    setRefreshing(true);
    apiFetch(`/api/dashboard${query ? `?${query}` : ''}`)
      .then(readApiPayload)
      .then((payload) => {
        if (cancelled) return;
        setDashboard(payload);
        const serverCompanies = getCollection(
          payload?.organization?.companies
          || payload?.companies
          || payload?.organization?.organizations,
        );
        setCompanies(serverCompanies);
        setError('');
      })
      .catch((requestError) => {
        if (!cancelled) setError(requestError.message || 'Dashboard non disponibile');
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedCompanyId, selectedLocationId, selectedDepartmentId, tenantContextVersion, isPlatformScope]);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setMobileMenuOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [mobileMenuOpen]);

  const organization = dashboard?.organization;
  const locationsSource = organization?.locations ?? dashboard?.locations ?? organization?.locales;
  const allLocations = getCollection(locationsSource);
  const locationsAvailable = locationsSource !== undefined && locationsSource !== null;
  const allDepartments = getCollection(
    organization?.departments
    || dashboard?.departments
    || organization?.reparti,
  );

  const visibleCompanies = companies.filter((company) => (
    matchesScopedItem(company, getCompanyId, effectiveScopeCompanyId, effectiveScopeCompany)
  ));
  const visibleLocations = allLocations.filter((location) => {
    const companyId = firstDefined(location?.company_id, location?.organization_id);
    const withinCompanyScope = !effectiveScopeCompanyId
      || (companyId && visibleCompanies.some((company) => sameValue(getCompanyId(company), companyId)));
    const withinLocationScope = matchesScopedItem(
      location,
      getLocationId,
      effectiveScopeLocationId,
      effectiveScopeLocation,
    );
    return withinCompanyScope
      && withinLocationScope
      && (selectedCompanyId === 'all' || !companyId || String(companyId) === String(selectedCompanyId));
  });
  const visibleDepartments = allDepartments.filter((department) => {
    const locationId = firstDefined(department?.location_id, department?.locale_id);
    const companyId = firstDefined(department?.company_id, department?.organization_id);
    const withinCompanyScope = !effectiveScopeCompanyId
      || (companyId && visibleCompanies.some((company) => sameValue(getCompanyId(company), companyId)));
    const withinLocationScope = !effectiveScopeLocationId
      || (locationId && visibleLocations.some((location) => sameValue(getLocationId(location), locationId)));
    const matchesLocation = selectedLocationId === 'all' || !locationId || String(locationId) === String(selectedLocationId);
    const matchesCompany = selectedCompanyId === 'all' || !companyId || String(companyId) === String(selectedCompanyId);
    return withinCompanyScope && withinLocationScope && matchesLocation && matchesCompany;
  });

  const people = dashboard?.people || {};
  const shifts = dashboard?.turni || dashboard?.shifts || {};
  const holidays = getCollection(dashboard?.ferie || dashboard?.holidays);
  const replacements = getCollection(dashboard?.sostituzioni || dashboard?.replacements);
  const canSeePersonale = hasCap(dashboardMe, 'personale.view');
  const canReadPersonnelDocuments = hasCap(dashboardMe, 'personale.documents.read')
    || hasCap(dashboardMe, 'employee.view')
    || hasCap(dashboardMe, 'personale.view')
    || hasCap(dashboardMe, 'personale.documents.manage');
  const canSeeTurni = hasCap(dashboardMe, 'turni.view') || hasCap(dashboardMe, 'availability.self.manage');
  const canManageFerie = hasAnyCapUnder(dashboardMe, 'ferie.approve')
    || hasAnyCapUnder(dashboardMe, 'ferie.balance')
    || hasAnyCapUnder(dashboardMe, 'ferie.config');
  const canSeeOwnFerie = hasCap(dashboardMe, 'ferie.view') || hasCap(dashboardMe, 'ferie.request');
  const canSeePerimeter = hasAnyCapUnder(dashboardMe, 'company')
    || hasAnyCapUnder(dashboardMe, 'location')
    || hasAnyCapUnder(dashboardMe, 'department')
    || canSeePersonale;
  const ferieRoute = canManageFerie ? '/ferie' : canSeeOwnFerie ? '/ferie-mie' : null;
  const todayShifts = firstDefined(shifts.today, shifts.today_count, shifts.in_program, shifts.in_programma);
  const activePeople = firstDefined(people.active, people.active_count, people.operational, people.operativi);
  const pendingHolidays = holidays.filter(isPending);
  const pendingReplacements = replacements.filter(isPending);
  const pendingHolidayCount = !dashboard || dashboard?.ferie?.status === 'unavailable'
    ? null
    : devSimulationActive
      ? null
    : firstDefined(getItemCount(dashboard?.ferie?.pending), pendingHolidays.length);
  const replacementCount = !dashboard || dashboard?.replacements?.status === 'unavailable'
    ? null
    : devSimulationActive
      ? null
    : firstDefined(getItemCount(dashboard?.replacements?.total), pendingReplacements.length);
  const attentionCount = (canManageFerie ? pendingHolidayCount || 0 : 0)
    + (canSeeTurni ? replacementCount || 0 : 0);
  const attentionUnavailable = (canManageFerie && pendingHolidayCount === null)
    || (canSeeTurni && replacementCount === null);

  const selectedCompany = visibleCompanies.find((company) => String(getCompanyId(company)) === String(selectedCompanyId));
  const selectedLocation = visibleLocations.find((location) => String(getLocationId(location)) === String(selectedLocationId));
  const selectedDepartment = visibleDepartments.find((department) => String(getDepartmentId(department)) === String(selectedDepartmentId));
  const handleExplorerBack = () => {
    if (selectedDepartmentId !== 'all') {
      setSelectedDepartmentId('all');
      return;
    }
    if (selectedLocationId !== 'all') {
      setSelectedLocationId('all');
      setSelectedDepartmentId('all');
      return;
    }
    if (selectedCompanyId !== 'all') {
      setSelectedCompanyId('all');
      setSelectedLocationId('all');
      setSelectedDepartmentId('all');
    }
  };
  const roleLabel = getRoleLabel(dashboardMe);
  const showDevTools = (
    process.env.NODE_ENV !== 'production'
    || process.env.REACT_APP_SHOW_DEV_TOOLS === 'true'
  ) && process.env.REACT_APP_SHOW_DEV_TOOLS !== 'false' && !!realMe?.is_global;
  const environmentRoleLabel = roleDisplayLabel({
    roleId: devImpersonateRoleId,
    personaId: devImpersonatePersonaId,
    realRole: getRoleLabel(realMe || me),
    roles: ruoli,
  });
  const personName = firstDefined(dashboardMe?.full_name, dashboardMe?.name, dashboardMe?.email, 'Titolare');
  const tenantScopeName = ceoTenantContextActive
    ? (ceoTenants.find((tenant) => tenant.id === activeTenantId)?.display_name || 'Tenant selezionato')
    : !devSimulationActive && realMe?.is_global
      ? 'Tutta la piattaforma'
      : null;
  const scopeName = tenantScopeName || (selectedLocation
    ? getName(selectedLocation)
    : selectedCompany
      ? getName(selectedCompany)
      : effectiveScopeLocation
        ? getName(effectiveScopeLocation)
        : effectiveScopeCompany
          ? getName(effectiveScopeCompany)
          : isPlatformScope ? 'Tutta la piattaforma' : 'Intero tenant');
  const tenantName = firstDefined(
    dashboardMe?.tenant_name,
    dashboardMe?.tenant_display_name,
    dashboard?.tenant_name,
    dashboard?.tenant?.name,
    visibleCompanies.length === 1 ? getName(visibleCompanies[0]) : null,
    !isPlatformScope && visibleCompanies.length > 1 ? `${visibleCompanies.length} aziende autorizzate` : null,
    !isPlatformScope ? 'Tenant operativo' : 'Tutta la piattaforma',
  );
  useEffect(() => {
    let cancelled = false;
    if (!canSeePersonale || isPlatformScope) {
      setPersonnelRows(null);
      setPersonnelLoading(false);
      setPersonnelError('');
      return () => {
        cancelled = true;
      };
    }

    setPersonnelLoading(true);
    apiFetch('/api/personale')
      .then(readApiPayload)
      .then((payload) => {
        if (!cancelled) {
          setPersonnelRows(getCollection(payload));
          setPersonnelError('');
        }
      })
      .catch((requestError) => {
        if (!cancelled) {
          setPersonnelRows(null);
          setPersonnelError(requestError.message || 'Elenco persone non disponibile');
        }
      })
      .finally(() => {
        if (!cancelled) setPersonnelLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canSeePersonale, tenantContextVersion, isPlatformScope]);

  const scopedPersonnelRows = Array.isArray(personnelRows)
    ? personnelRows.filter((person) => {
      const companyMatches = !effectiveScopeCompany
        || sameValue(person.company_id, effectiveScopeCompanyId)
        || sameValue(person.company_name, getName(effectiveScopeCompany))
        || visibleCompanies.some((company) => sameValue(person.company_name, getName(company)));
      const locationMatches = !effectiveScopeLocation
        || sameValue(person.location_id, effectiveScopeLocationId)
        || sameValue(person.primary_location, getName(effectiveScopeLocation))
        || visibleLocations.some((location) => sameValue(person.primary_location, getName(location)));
      return companyMatches && locationMatches;
    })
    : personnelRows;
  const activePeopleForUi = !canSeePersonale
    ? null
    : Array.isArray(scopedPersonnelRows)
      ? countActivePeople(scopedPersonnelRows)
      : devSimulationActive && effectiveScopeCompanyId
        ? visibleCompanies.reduce((total, company) => {
          const companyPeople = people?.by_company?.[getCompanyId(company)]
            || people?.by_company?.[String(getCompanyId(company))];
          const count = getItemCount(companyPeople?.active ?? companyPeople);
          return total + (count === null ? 0 : count);
        }, 0)
        : activePeople;
  const todayShiftsForUi = !hasCap(dashboardMe, 'turni.view') || devSimulationActive ? null : todayShifts;
  const peopleForUi = canSeePersonale ? people : {};

  const managementItems = [
    ...(canSeePersonale ? [{ label: 'Personale', icon: Users, detail: 'Anagrafica e assegnazioni', to: '/personale' }] : []),
    ...(canSeeTurni ? [{ label: 'Turni', icon: CalendarDays, detail: 'Pianificazione operativa', to: '/turni' }] : []),
    ...(ferieRoute ? [{ label: 'Ferie', icon: Archive, detail: 'Richieste e approvazioni', to: ferieRoute }] : []),
    ...(canSeeTurni ? [{ label: 'Sostituzioni', icon: UserRound, detail: 'Copertura dei turni', to: '/turni' }] : []),
    ...(hasCap(dashboardMe, 'attendance.view') || hasCap(dashboardMe, 'attendance.correct')
      ? [{
        label: 'Presenze',
        icon: ClipboardCheck,
        detail: 'Rilevazione e storico',
        to: '/presenze',
        testId: 'dashboard-attendance-module',
      }]
      : []),
    ...(hasCap(dashboardMe, 'security.users.view')
      ? [{ label: 'Utenti e Accessi', icon: ShieldAlert, detail: 'Accessi operativi del tenant', to: '/personale', testId: 'dashboard-users-access-module' }]
      : []),
  ];
  const managementSidebarItems = managementItems.slice(0, 5);
  if (canReadPersonnelDocuments && !isPlatformScope) {
    const personnelIndex = managementSidebarItems.findIndex((item) => item.label === 'Personale');
    managementSidebarItems.splice(personnelIndex >= 0 ? personnelIndex + 1 : 0, 0, {
      label: 'Documenti del personale',
      icon: Files,
      to: '/documenti-personale',
      testId: 'dashboard-sidebar-personnel-documents',
    });
  }
  const accessManagementItem = isPlatformScope
    ? { label: 'Gestione Titolari', icon: ShieldAlert, to: '/foundation/users' }
    : { label: 'Utenti e Accessi', icon: ShieldAlert, to: '/personale' };
  const operationalItems = managementItems.filter((item) => (
    ['Turni', 'Ferie', 'Personale', 'Presenze'].includes(item.label)
  ));

  const handleLogout = async () => {
    try {
      await apiFetch('/api/logout', { method: 'POST' });
    } finally {
      window.location.assign('/login');
    }
  };

  const sidebarSections = [
    {
      title: 'Gestione',
      items: managementSidebarItems,
    },
    {
      title: 'Organizzazione',
      items: [
        ...(hasAnyCapUnder(dashboardMe, 'company') || hasAnyCapUnder(dashboardMe, 'location') || hasAnyCapUnder(dashboardMe, 'department')
          ? [{ label: 'Struttura aziendale', icon: Building2, to: '/impostazioni/struttura-aziendale' }]
          : []),
        ...(hasCap(dashboardMe, 'security.users.view')
          ? [accessManagementItem]
          : []),
        ...(hasAnyCapUnder(dashboardMe, 'department') || hasAnyCapUnder(dashboardMe, 'ferie.config')
          ? [{ label: 'Impostazioni', icon: SlidersHorizontal, to: '/impostazioni' }]
          : []),
      ],
    },
    {
      title: 'Controllo',
      items: [
        ...(NFC_UI_VISIBLE && hasCap(dashboardMe, 'attendance.*')
          ? [{ label: 'Punti NFC', icon: ClipboardCheck, to: '/impostazioni/punti-nfc' }]
          : []),
        ...(hasCap(dashboardMe, 'audit.view')
          ? [{ label: 'Tracciabilità', icon: FileText, to: '/audit' }]
          : []),
      ],
    },
    {
      title: 'Account',
      items: [
        { label: 'Profilo', icon: UserRound, detail: 'Gestito dal menu account', disabled: true },
        { label: 'Notifiche', icon: Bell, detail: 'Usa il campanello in alto', disabled: true },
        { label: 'Assistenza', icon: CircleCheck, detail: 'In arrivo', disabled: true },
        { label: 'Esci', icon: ArrowLeft, action: handleLogout },
      ],
    },
  ].filter((section) => section.items.length > 0);
  const dashboardMobileSection = {
    title: 'Dashboard',
    items: [{ label: 'Dashboard', icon: LayoutDashboard, to: '/foundation', testId: 'dashboard-sidebar-home-mobile' }],
  };
  const mobileSidebarSections = isPlatformScope
    ? [
      {
        title: 'Piattaforma',
        items: [
          { label: 'Dashboard', icon: LayoutDashboard, to: '/foundation', testId: 'dashboard-sidebar-home-mobile' },
          { label: 'Tenant / Clienti', icon: Building2, to: '/foundation#tenant-clienti', testId: 'dashboard-tenant-switcher-mobile' },
        ],
      },
      ...sidebarSections
        .filter((section) => section.title === 'Controllo' || section.title === 'Account')
        .map((section) => section.title === 'Controllo'
          ? { ...section, items: section.items.filter((item) => item.to === '/audit') }
          : section)
        .filter((section) => section.items.length > 0),
    ]
    : [dashboardMobileSection, ...sidebarSections];

  const setCompany = (value) => {
    setSelectedCompanyId(value);
    setSelectedLocationId('all');
    setSelectedDepartmentId('all');
  };

  const setLocation = (value) => {
    setSelectedLocationId(value);
    setSelectedDepartmentId('all');
  };

  const reportRows = getCollection(dashboard?.reports || dashboard?.report || dashboard?.recent_reports);

  if (loading && !dashboard) {
    return (
      <div className="cc-shell cc-loading-shell">
        <div className="cc-loading-bar" />
        <div className="cc-loading-grid" aria-label="Caricamento Dashboard">
          <div /><div /><div /><div />
        </div>
      </div>
    );
  }

  return (
    <div className={`cc-shell ${desktopSidebarOpen ? 'has-desktop-sidebar' : ''}`} data-testid="foundation-dashboard">
      {desktopSidebarOpen && (
        <DashboardSidebar
          sections={sidebarSections}
          onClose={() => {
            setDesktopSidebarOpen(false);
            setMobileMenuOpen(false);
          }}
        />
      )}

      <div className="cc-main-area">
        <header className="cc-header" data-testid="dashboard-topbar">
          <div className="cc-header-left">
            {!desktopSidebarOpen && (
              <button
                type="button"
                className="cc-menu-trigger"
                aria-label="Apri navigazione"
                aria-expanded={desktopSidebarOpen || mobileMenuOpen}
                onClick={() => {
                  const isDesktop = typeof window === 'undefined'
                    || typeof window.matchMedia !== 'function'
                    || window.matchMedia('(min-width: 768px)').matches;
                  setDesktopSidebarOpen(isDesktop);
                  setMobileMenuOpen(true);
                }}
                data-testid="dashboard-menu-trigger"
              >
                <MenuIcon size={19} aria-hidden="true" />
              </button>
            )}
            {!desktopSidebarOpen && (
              <Link className="cc-brand" to="/foundation" aria-label="RestaurantOS Dashboard">
                <img src="/brand/restaurantos-logo-horizontal.svg" alt="RestaurantOS" />
              </Link>
            )}
          </div>

          <div className="cc-header-center cc-header-page" data-testid="dashboard-page-context">
            <strong>Dashboard</strong>
            {!isPlatformScope && (
              <span className="cc-header-page-subtitle">Titolare · {personName}</span>
            )}
          </div>

          <div className="cc-header-right">
          {realMe?.is_global && (showDevTools ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="cc-env-role cc-env-role-button"
                  type="button"
                  aria-label={`Ruolo: ${environmentRoleLabel}`}
                  data-testid="dashboard-environment-role"
                >
                  <span className="cc-env-dot" aria-hidden="true" />
                  <span className="cc-env-text">{environmentRoleLabel}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 max-h-[70vh] overflow-auto">
                <DropdownMenuLabel className="text-xs">Simula ruolo</DropdownMenuLabel>
                <p className="px-2 py-1 text-xs text-stone-500">Preview visuale e di navigazione, non verifica dei permessi server.</p>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="dashboard-impersona-none" onClick={() => impersona(null)}>Torna a CEO</DropdownMenuItem>
                {ruoli.filter((role) => role.id !== 'ceo').map((role) => (
                  <DropdownMenuItem key={role.id} data-testid={`dashboard-impersona-${role.id}`} onClick={() => impersona(role.id)}>
                    {role.nome}{role.id === 'dipendente' ? ' · simulazione generica' : ''}
                  </DropdownMenuItem>
                ))}
                {!isPlatformScope && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger data-testid="dashboard-simula-persona-trigger" className="text-xs">
                        Simula persona reale…
                      </DropdownMenuSubTrigger>
                      <DropdownMenuPortal>
                        <DropdownMenuSubContent className="w-64 max-h-[60vh] overflow-auto">
                          <DropdownMenuLabel className="text-[11px] text-stone-500">
                            Read-only · nessuna scrittura DB
                          </DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          {[...dipendenti]
                            .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''))
                            .map((employee) => (
                              <DropdownMenuItem
                                key={employee.id}
                                data-testid={`dashboard-simula-persona-${employee.id}`}
                                onClick={() => impersonaPersona(employee.id)}
                                className="text-xs"
                              >
                                {employee.displayName}
                                <span className="ml-auto text-[10px] text-stone-400 font-mono">{employee.id}</span>
                              </DropdownMenuItem>
                            ))}
                        </DropdownMenuSubContent>
                      </DropdownMenuPortal>
                    </DropdownMenuSub>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <span className="cc-env-role" aria-label={`Ruolo: ${environmentRoleLabel}`} data-testid="dashboard-environment-role">
              <span className="cc-env-dot" aria-hidden="true" />
              <span className="cc-env-text">{environmentRoleLabel}</span>
            </span>
          ))}
          {isPlatformScope && <span className="cc-scope-context"><span className="cc-scope-dot" /> {roleLabel} <span className="cc-divider">·</span> Scope: {scopeName}</span>}
          <NotificationsBell />
          <div className="cc-profile" data-testid="dashboard-header-context">
            <button
              className="cc-profile-button"
              type="button"
              aria-expanded={profileOpen}
              aria-haspopup="menu"
              onClick={() => setProfileOpen((open) => !open)}
            >
              <span className="cc-avatar">{getInitials(dashboardMe)}</span>
              <span className="cc-profile-copy">
                <strong>{personName}</strong>
                <small>{roleLabel}</small>
              </span>
              <ChevronDown size={15} />
            </button>
            {profileOpen && (
              <div className="cc-profile-menu" role="menu">
                {!isPlatformScope && (hasAnyCapUnder(dashboardMe, 'department') || hasAnyCapUnder(dashboardMe, 'ferie.config')) ? (
                  <Link to="/impostazioni" role="menuitem" onClick={() => setProfileOpen(false)}>Impostazioni</Link>
                ) : null}
                <button type="button" role="menuitem" onClick={handleLogout}>Esci</button>
              </div>
            )}
          </div>
        </div>
        </header>

      <main className="cc-main">
        <div className="cc-page-intro">
          <div>
            <span className="cc-page-kicker">Dashboard</span>
            <h1>Buongiorno, {isPlatformScope ? 'Founder' : personName} <span>· {new Intl.DateTimeFormat('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(new Date())}</span></h1>
            {!isPlatformScope && <p>Ecco la situazione operativa del tuo gruppo.</p>}
          </div>
          {refreshing && <span className="cc-refreshing">Aggiornamento…</span>}
        </div>

        {error && (
          <div className="cc-alert cc-alert-error" role="alert" data-testid="foundation-error">
            <ShieldAlert size={18} />
            <span>{error}</span>
          </div>
        )}

        {!isPlatformScope && (
          <section className="cc-tenant-banner" data-testid="dashboard-tenant-header">
            <div className="cc-tenant-badge" aria-hidden="true"><Building2 size={17} /></div>
            <div className="cc-tenant-copy">
              <span>Tenant corrente</span>
              <strong>{tenantName}</strong>
              <small>Ruolo {roleLabel} · perimetro {scopeName}</small>
            </div>
            <span className="cc-tenant-status"><CircleCheck size={14} /> Solo tenant autorizzato</span>
          </section>
        )}

        {isPlatformScope && (
          <TenantClientSwitcher
            tenants={ceoTenants}
            loading={ceoTenantsLoading}
            activeTenantId={activeTenantId}
            busy={tenantContextBusy}
            onEnter={enterTenant}
            onManageAccess={manageTenantAccess}
          />
        )}

        {ceoTenantContextActive && (
          <section className="cc-card flex flex-wrap items-center justify-between gap-3" data-testid="ceo-active-tenant">
            <div>
              <span className="cc-panel-label">CONTESTO TENANT ATTIVO</span>
              <strong className="block text-brand-navy">CEO · {scopeName}</strong>
            </div>
            <button
              type="button"
              className="cc-section-link"
              onClick={exitTenant}
              disabled={tenantContextBusy}
              data-testid="ceo-exit-tenant"
            >
              ← Torna alla piattaforma
            </button>
          </section>
        )}

        {!isPlatformScope && <div className="cc-dashboard-columns">
          <div className="cc-primary-column">
        <section className="cc-card cc-today">
          <div className="cc-panel-heading">
            <div>
              <span className="cc-panel-label">OPERATIVITÀ</span>
              <h2>Situazione operativa</h2>
            </div>
            <span className="cc-mock-label">Dati server-driven</span>
          </div>
          <div className="cc-kpi-grid" aria-label="Indicatori principali">
            {hasCap(dashboardMe, 'turni.view') && (
              <MetricCard
                icon={CalendarDays}
                label="Turni"
                value={formatKpiValue(getItemCount(todayShiftsForUi))}
                detail={getItemCount(todayShiftsForUi) === null ? 'Dato non disponibile' : 'Oggi'}
                tone="violet"
                testId="dashboard-kpi-shifts"
              />
            )}
            <MetricCard
              icon={Users}
              label="Persone"
              value={formatKpiValue(getItemCount(activePeopleForUi))}
              detail={getItemCount(activePeopleForUi) === null ? 'Dato non disponibile' : 'Nel perimetro autorizzato'}
              tone="blue"
              testId="dashboard-kpi-people"
            />
            <MetricCard
              icon={Building2}
              label="Locali"
              value={hasCap(dashboardMe, 'location.view') && locationsAvailable
                ? formatKpiValue(visibleLocations.length)
                : KPI_UNAVAILABLE}
              detail={hasCap(dashboardMe, 'location.view') && locationsAvailable
                ? 'Nel perimetro autorizzato'
                : 'Dato non disponibile'}
              tone="teal"
              testId="dashboard-kpi-locations"
            />
            <MetricCard
              icon={ClipboardCheck}
              label="Presenze"
              value={KPI_UNAVAILABLE}
              detail="Dato non disponibile"
              tone="amber"
              testId="dashboard-kpi-attendance"
            />
          </div>
        </section>

        <section className={`cc-check ${attentionCount > 0 ? 'cc-check-warning' : ''}`} aria-label="Da controllare" data-testid="dashboard-attention">
          <div className="cc-check-icon">
            {attentionUnavailable ? <ShieldAlert size={18} /> : attentionCount > 0 ? <ShieldAlert size={18} /> : <CircleCheck size={18} />}
          </div>
          <div>
            <span className="cc-panel-label">DA CONTROLLARE</span>
            <strong>
              {attentionUnavailable
                ? UNAVAILABLE
                : attentionCount > 0
                  ? `${formatInteger(attentionCount)} ${attentionCount === 1 ? 'attività da verificare' : 'attività da verificare'}`
                  : 'Tutto sotto controllo'}
            </strong>
            <p>
              {attentionUnavailable
                ? 'Lo stato delle attività non è esposto dal server.'
                : attentionCount > 0
                  ? 'Ferie e sostituzioni richiedono una revisione.'
                  : 'Nessuna attività urgente nelle prossime ore.'}
            </p>
          </div>
          <div className="cc-check-time">Ambito <b>{scopeName}</b></div>
          {attentionCount > 0 && !attentionUnavailable && (
            <div className="cc-check-list">
              {canManageFerie && pendingHolidayCount > 0 && (
                <Link className="cc-check-row" to="/ferie">
                  <span>Ferie da approvare</span>
                  <b>{formatInteger(pendingHolidayCount)} <ArrowRight size={14} /></b>
                </Link>
              )}
              {canSeeTurni && replacementCount > 0 && (
                <Link className="cc-check-row" to="/turni">
                  <span>Sostituzioni da verificare</span>
                  <b>{formatInteger(replacementCount)} <ArrowRight size={14} /></b>
                </Link>
              )}
            </div>
          )}
        </section>

        <SectionList
          title="Azioni rapide"
          items={managementItems}
          className="cc-quick-actions"
          testId="dashboard-quick-actions"
        />

        <OperationalOverview items={operationalItems} />

          {canSeePerimeter ? (
            <PerimeterExplorer
              visibleCompanies={visibleCompanies}
              visibleLocations={visibleLocations}
              visibleDepartments={visibleDepartments}
              selectedCompanyId={selectedCompanyId}
              selectedLocationId={selectedLocationId}
              selectedDepartmentId={selectedDepartmentId}
              selectedCompany={selectedCompany}
              selectedLocation={selectedLocation}
              selectedDepartment={selectedDepartment}
              locationsAvailable={locationsAvailable}
              people={peopleForUi}
              activePeople={activePeopleForUi}
              personnelRows={scopedPersonnelRows}
              personnelLoading={personnelLoading}
              personnelError={personnelError}
              canSeePersonale={canSeePersonale}
              onSelectCompany={setCompany}
              onSelectLocation={setLocation}
              onSelectDepartment={setSelectedDepartmentId}
              onBack={handleExplorerBack}
              onReset={() => {
                setSelectedCompanyId('all');
                setSelectedLocationId('all');
                setSelectedDepartmentId('all');
              }}
              onClearLocation={() => {
                setSelectedLocationId('all');
                setSelectedDepartmentId('all');
              }}
              onClearDepartment={() => setSelectedDepartmentId('all')}
            />
          ) : (
            <section className="cc-card cc-explorer" data-testid="dashboard-perimeter-unavailable">
              <div className="cc-panel-heading">
                <div>
                  <span className="cc-panel-label">Perimetro aziendale</span>
                  <h2>Non disponibile per questo ruolo</h2>
                </div>
              </div>
              <EmptyState>
                <ShieldAlert size={18} />
                <span><strong>Perimetro amministrativo non disponibile</strong><small>La Dashboard mostra solo le funzioni previste dalle capability effettive.</small></span>
              </EmptyState>
            </section>
          )}

          {hasCap(dashboardMe, 'audit.view') && (
            <Link className="cc-trace-link" to="/audit">
              <span><ClipboardCheck size={16} /><strong>Tracciabilità</strong><small>Consulta il registro completo delle attività autorizzate.</small></span>
              <ArrowRight size={17} />
            </Link>
          )}
          </div>

          <aside className="cc-secondary-column">
          <StructureSummary
            companies={dashboard ? visibleCompanies.length : null}
            locations={locationsAvailable ? visibleLocations.length : null}
            departments={dashboard && hasCap(dashboardMe, 'department.view') ? visibleDepartments.length : null}
            people={getItemCount(peopleForUi.total ?? activePeopleForUi)}
            canOpen={canSeePerimeter}
          />

          {!isPureEmployee(dashboardMe) && (
            <section className="cc-card cc-reports" data-testid="dashboard-recent-reports">
              <SectionHeader eyebrow="LETTURE RAPIDE" title="Report recenti" actionLabel="Vedi tutti" />
              {reportRows.length > 0 ? (
                <div className="cc-report-list">
                  {reportRows.slice(0, 4).map((report, index) => (
                    <div className="cc-report-row" key={report.id || `${report.name || 'report'}-${index}`}>
                      <span className="cc-report-icon"><FileText size={16} /></span>
                      <span><strong>{getName(report, 'Report')}</strong><small>{formatDate(report.created_at || report.date)}</small></span>
                      {firstDefined(report.value, report.summary) && <b className="cc-report-value">{firstDefined(report.value, report.summary)}</b>}
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState><FileText size={18} /><span><strong>Nessun report recente</strong><small>I report disponibili appariranno qui.</small></span></EmptyState>
              )}
            </section>
          )}
          </aside>
        </div>}

        {!isPureEmployee(dashboardMe) && (!isPlatformScope || isGlobalCeo(dashboardMe)) && (
          <CompanyArea me={dashboardMe} onlyHaccp={isPlatformScope} />
        )}

        <div className="cc-footnote"><ShieldAlert size={13} /> Il perimetro e i dati mostrati provengono dal server e rispettano le autorizzazioni dell'account.</div>
      </main>
      </div>

      {mobileMenuOpen && (
        <>
          <button className="cc-sheet-backdrop" type="button" aria-label="Chiudi menu" onClick={() => setMobileMenuOpen(false)} />
          <div className="cc-mobile-sheet" role="dialog" aria-modal="true" aria-label="Menu Dashboard">
            <div className="cc-mobile-sheet-header">
              <div><span>Menu</span><strong>Navigazione</strong></div>
              <button type="button" className="cc-icon-button" onClick={() => setMobileMenuOpen(false)} aria-label="Chiudi menu"><X size={18} /></button>
            </div>
            {mobileSidebarSections.map((section) => (
              <section className="cc-menu-group" key={section.title}>
                <small>{section.title}</small>
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const testId = item.testId ? `${item.testId}-mobile` : undefined;
                  const isActive = Boolean(item.to && (
                    location.pathname === item.to
                    || (item.to === '/foundation' && location.pathname === '/foundation')
                  ));
                  if (item.action) {
                    return (
                      <button
                        key={item.label}
                        type="button"
                        className="cc-menu-action"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          item.action();
                        }}
                        data-testid={testId}
                      >
                        <Icon size={16} /> {item.label}
                      </button>
                    );
                  }
                  if (item.disabled || !item.to) {
                    return (
                      <span key={item.label} className="cc-menu-disabled" aria-disabled="true" data-testid={testId}>
                        <Icon size={16} /> <span>{item.label}</span>
                        {item.detail && <b>{item.detail}</b>}
                      </span>
                    );
                  }
                  return (
                    <Link
                      key={item.label}
                      to={item.to}
                      onClick={() => setMobileMenuOpen(false)}
                      className={isActive ? 'is-active' : undefined}
                      aria-current={isActive ? 'page' : undefined}
                      data-testid={testId}
                    >
                      <Icon size={16} /> <span>{item.label}</span> <ArrowRight size={14} />
                    </Link>
                  );
                })}
              </section>
            ))}
          </div>
        </>
      )}

      <nav className="cc-bottom-nav ros-bottom-nav" aria-label="Navigazione principale">
        <Link className="is-active" to="/foundation"><LayoutDashboard size={19} /><span>Dashboard</span></Link>
        {!isPlatformScope && ferieRoute && <Link to={ferieRoute}><CalendarDays size={19} /><span>Ferie</span></Link>}
        {!isPlatformScope && canSeePersonale && <Link to="/personale"><Users size={19} /><span>Personale</span></Link>}
        {!isPlatformScope && canSeeTurni && <Link to="/turni"><Clock3 size={19} /><span>Turni</span></Link>}
        <button type="button" onClick={() => setMobileMenuOpen((open) => !open)} aria-expanded={mobileMenuOpen}><MenuIcon size={19} /><span>Menu</span></button>
      </nav>
    </div>
  );
}

function FoundationDashboard() {
  const dashboardMe = useEffectiveMe();
  if (isPureEmployee(dashboardMe)) return <EmployeeDashboard />;
  return <AdminFoundationDashboard />;
}

export default FoundationDashboard;